// src/data/wprClient.js
//
// WPR Slice 3 + WPR-SNAPSHOT — client for /api/wpr editorial update,
// week-navigation, and snapshot layer.
//
// All methods are async and return camelCase domain objects.
// No caching: updates are short-lived editorial text; stale reads are bad UX.

import {
  AuthError,
  NetworkError,
  ValidationError,
  errorFromResponse,
} from "./errors.js";
import { log } from "../utils/logger.js";

export { ValidationError };

const BASE = "/api/wpr";

async function apiFetch(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (err) {
    throw new NetworkError(`Network error: ${err.message}`);
  }
  if (!res.ok) {
    throw await errorFromResponse(res);
  }
  return res.json();
}

/**
 * Fetch all WPR editorial update records for the given week.
 *
 * @param {string|null} weekDate  ISO date string for Monday of the target week
 *                                (YYYY-MM-DD). Omit to default to current Monday.
 * @returns {Promise<Array>}  array of { id, entityId, entityType, updateText,
 *                            updatedAt, updatedBy, weekDate }
 */
export async function getWprUpdates(weekDate = null) {
  const url = weekDate
    ? `${BASE}/updates?weekDate=${encodeURIComponent(weekDate)}`
    : `${BASE}/updates`;
  const data = await apiFetch(url);
  if (!Array.isArray(data)) {
    log.warn("wprClient.getWprUpdates: unexpected non-array response");
    return [];
  }
  return data;
}

/**
 * Fetch the list of weeks that have at least one update, plus the current week.
 * Always sorted newest-first.
 *
 * @returns {Promise<Array>}  array of {
 *   weekDate: "YYYY-MM-DD",
 *   frozen:   boolean,
 *   frozenAt: ISO timestamp string | null,
 *   frozenBy: string | null,
 * }
 */
export async function getWprWeeks() {
  const data = await apiFetch(`${BASE}/weeks`);
  if (!Array.isArray(data)) {
    log.warn("wprClient.getWprWeeks: unexpected non-array response");
    return [];
  }
  return data;
}

/**
 * Upsert the editorial update text for a single roll-up row in the given week.
 * Requires editor+ role.
 *
 * @param {"project"|"location"} entityType
 * @param {string}               entityId    UUID of the project or location
 * @param {string}               updateText  editorial one-liner (max 2 000 chars)
 * @param {string}               weekDate    Monday of the target week (YYYY-MM-DD)
 * @returns {Promise<object>}    the saved record (camelCase)
 */
export async function upsertWprUpdate(entityType, entityId, updateText, weekDate) {
  if (!["project", "location"].includes(entityType)) {
    throw new ValidationError(`upsertWprUpdate: invalid entityType "${entityType}"`);
  }
  if (!entityId || typeof entityId !== "string") {
    throw new ValidationError("upsertWprUpdate: entityId is required");
  }
  if (!weekDate || typeof weekDate !== "string") {
    throw new ValidationError("upsertWprUpdate: weekDate is required");
  }
  return apiFetch(
    `${BASE}/updates/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}`,
    {
      method:  "PATCH",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ updateText, weekDate }),
    },
  );
}

/**
 * Freeze the given week so no further editorial edits can be made.
 * Assembles and persists the snapshot at freeze time.
 * Requires editor+ role.
 *
 * @param {string} weekDate  Monday of the week to freeze (YYYY-MM-DD)
 * @returns {Promise<object>}  { weekDate, frozenAt, frozenBy }
 */
export async function freezeWprWeek(weekDate) {
  if (!weekDate || typeof weekDate !== "string") {
    throw new ValidationError("freezeWprWeek: weekDate is required");
  }
  return apiFetch(`${BASE}/freeze`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ weekDate }),
  });
}

/**
 * Legacy compatibility request. Calendar policy rejects manual unfreeze.
 * Retained snapshots are not deleted by this route.
 *
 * @param {string} weekDate  Monday of the week to unfreeze (YYYY-MM-DD)
 * @returns {Promise<{ weekDate: string, unfrozenAt: string }>}
 */
export async function unfreezeWprWeek(weekDate) {
  if (!weekDate || typeof weekDate !== "string") {
    throw new ValidationError("unfreezeWprWeek: weekDate is required");
  }
  return apiFetch(`${BASE}/unfreeze`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ weekDate }),
  });
}

/**
 * Legacy compatibility request. Calendar policy rejects snapshot replacement.
 *
 * @param {string} weekDate  Monday of the week to re-freeze (YYYY-MM-DD)
 * @returns {Promise<{ weekDate: string, frozenAt: string, frozenBy: string }>}
 */
export async function refreezeWprWeek(weekDate) {
  if (!weekDate || typeof weekDate !== "string") {
    throw new ValidationError("refreezeWprWeek: weekDate is required");
  }
  return apiFetch(`${BASE}/refreeze`, {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ weekDate }),
  });
}

/**
 * Fetch the snapshot payload for a frozen week.
 * Viewer+. Only call when isFrozen === true for the selected week.
 *
 * @param {string} weekDate  Monday of the frozen week (YYYY-MM-DD)
 * @returns {Promise<{ weekDate, payload, schemaVersion, frozenAt, frozenBy }>}
 */
export async function getWprSnapshot(weekDate) {
  if (!weekDate || typeof weekDate !== "string") {
    throw new ValidationError("getWprSnapshot: weekDate is required");
  }
  return apiFetch(`${BASE}/snapshot?weekDate=${encodeURIComponent(weekDate)}`);
}
